import { useCallback, useState } from 'react';
import { ToastType } from 'src/components/atoms/CustomToast';

interface ToastState {
  isOpen: boolean;
  title: string;
  description: string;
  type: ToastType;
  duration: number;
}

const initialToastState: ToastState = {
  isOpen: false,
  title: '',
  description: '',
  type: 'success',
  duration: 5000,
};

export const useCustomToast = () => {
  const [toastState, setToastState] = useState<ToastState>(initialToastState);

  const showToast = useCallback(
    (
      title: string,
      description: string,
      type: ToastType = 'success',
      duration: number = 5000,
    ) => {
      setToastState({
        isOpen: true,
        title,
        description,
        type,
        duration,
      });
    },
    [],
  );

  const hideToast = useCallback(() => {
    setToastState((prev) => ({
      ...prev,
      isOpen: false,
    }));
  }, []);

  const showSuccess = useCallback(
    (title: string, description: string, duration?: number) => {
      showToast(title, description, 'success', duration);
    },
    [showToast],
  );

  const showError = useCallback(
    (title: string, description: string, duration?: number) => {
      showToast(title, description, 'error', duration);
    },
    [showToast],
  );

  const showWarning = useCallback(
    (title: string, description: string, duration?: number) => {
      showToast(title, description, 'warning', duration);
    },
    [showToast],
  );

  const showInfo = useCallback(
    (title: string, description: string, duration?: number) => {
      showToast(title, description, 'info', duration);
    },
    [showToast],
  );

  return {
    toastState,
    showToast,
    hideToast,
    showSuccess,
    showError,
    showWarning,
    showInfo,
  };
};
